/**
 * Service that detects if playback is progressing at a normal rate.
 *
 * This accounts for various playback rates without needing to know the actual playback rate.
 */
export class PlaybackProgressDetectionService {
  private lastEventTimestamp: number | undefined;
  private lastCurrentTime: number | undefined;

  /**
   * @param eventTimestamp ISO 8601 timestamp string when the timeupdate event fired
   * @param currentTime Player's current playback position in seconds
   * @returns true if currentTime delta is within 0.5x to 4x of elapsed real time, false otherwise
   */
  hasPlaybackProgressed(eventTimestamp: number, currentTime: number): boolean {
    // First event: establish baseline
    if (this.lastEventTimestamp === undefined || this.lastCurrentTime === undefined) {
      this.lastEventTimestamp = eventTimestamp;
      this.lastCurrentTime = currentTime;
      return false;
    }

    const elapsedMs = eventTimestamp - this.lastEventTimestamp;
    const elapsedSec = elapsedMs / 1000;
    const currentTimeDelta = currentTime - this.lastCurrentTime;

    // No elapsed time or backwards playback (seek): can't determine
    if (elapsedSec <= 0 || currentTimeDelta < 0) {
      this.lastEventTimestamp = eventTimestamp;
      this.lastCurrentTime = currentTime;
      return false;
    }

    // Check if currentTime delta is within 0.5x to 4x of elapsed time
    // This range accounts for common playback rates (0.5x, 1x, 2x, 4x) without knowing actual rate
    const lowerBound = elapsedSec * 0.5;
    const upperBound = elapsedSec * 4;
    const isValidProgress = currentTimeDelta >= lowerBound && currentTimeDelta <= upperBound;

    this.lastEventTimestamp = eventTimestamp;
    this.lastCurrentTime = currentTime;

    return isValidProgress;
  }

  /**
   * Resets the service state, clearing baseline data.
   */
  reset(): void {
    this.lastEventTimestamp = undefined;
    this.lastCurrentTime = undefined;
  }
}
